import { Injectable } from '@angular/core';
import { combineLatest, forkJoin, map, Observable } from 'rxjs';
import { I18nDataCache } from './i18n-data.cache';
import { KvdsCache } from './kvds.cache';
import { WardsCache } from './wards.cache';

export const CACHES = ['i18nData', 'kvds', 'wards'] as const;
export type CacheKey = (typeof CACHES)[number];
export type CacheRecord<T> = Partial<Record<CacheKey, T>>;

@Injectable({ providedIn: 'root' })
export class CachesService {
    constructor(
        public readonly i18nData: I18nDataCache,
        public readonly kvds: KvdsCache,
        public readonly wards: WardsCache,
    ) {}

    /**
     * Update some or all caches
     * @param cachesToUpdate Which caches should be updated
     * @param optIn Determines if `caches` is opt-in (`true`, default) or opt-out (`false`)
     * @returns Observable of values emitted by update() of the caches defined by `caches` and `optIn` is currently loading
     */
    updateCaches(cachesToUpdate: CacheRecord<boolean> = {}, optIn = true) {
        const observables = CACHES.reduce<CacheRecord<Observable<unknown>>>((acc, key) => {
            const explicitlyTrue = !!cachesToUpdate[key];
            const notOptedOut = !optIn && !(key in cachesToUpdate);

            if (explicitlyTrue || notOptedOut) {
                acc[key] = this[key].update();
                return acc;
            }

            return acc;
        }, {});

        return forkJoin(observables);
    }

    /**
     * Check the specified caches are currently loading
     * @param combine If `true` (default) combines individual loading infos to single boolean
     * @param caches Which caches should be watched
     * @param optIn Determines if `caches` is opt-in (`true`, default) or opt-out (`false`), i.e. if caches not included in `caches` are implicitly included in result (`opt-out`) or not (`opt-in`, default)
     * @returns Observable of boolean or CacheRecord<boolean> (see `combine`) if any of the caches defined by `caches` and `optIn` is currently loading
     */
    loadingCaches(combine?: true, caches?: CacheRecord<boolean>, optIn?: boolean): Observable<boolean>;
    loadingCaches(combine?: false, caches?: CacheRecord<boolean>, optIn?: boolean): Observable<CacheRecord<boolean>>;
    loadingCaches(combine = true, cachesToWatch: CacheRecord<boolean> = {}, optIn = true) {
        const observables = CACHES.reduce<CacheRecord<Observable<boolean>>>((acc, key) => {
            const explicitlyTrue = !!cachesToWatch[key];
            const notOptedOut = !optIn && !(key in cachesToWatch);

            if (explicitlyTrue || notOptedOut) {
                acc[key] = this[key].loadingObs;
                return acc;
            }

            return acc;
        }, {});

        if (!combine) {
            return combineLatest(observables);
        }

        return combineLatest(observables).pipe(map((loadingDict) => Object.values(loadingDict).some((loading) => loading)));
    }
}
